/**
 * Phase BACKGROUND-ANALYSIS-P1a.5-vi — retry orchestration hook.
 *
 * Wraps regenerateOutfit with:
 *  - sessionStorage attempt counter (key: regen_attempts:<outfitId>)
 *  - 3-attempt hard cap (CTA hides via canRetry=false beyond cap)
 *  - 2s backoff if the previous retry failed within last 10s
 *  - debounce against concurrent retries while status === 'retrying'
 *  - analytics: trackEvent('outfit_retry', { ... })
 *
 * Counter is incremented ONLY after a successful dispatch (Memory #8:
 * cost guard — a failed dispatch must not burn a budget slot).
 */
import { useCallback, useRef, useState } from "react";
import { trackEvent } from "@/lib/analytics";
import { regenerateOutfit } from "@/lib/regenerateOutfit";

const MAX_ATTEMPTS = 3;
const BACKOFF_WINDOW_MS = 10_000;
const BACKOFF_DELAY_MS = 2_000;

export type RetryTrigger = "stuck" | "failed" | "load_failed";
export type RetryStatus = "idle" | "retrying" | "dispatched" | "error";

export interface UseRetryAnalysisReturn {
  retry: (trigger: RetryTrigger) => Promise<{ newOutfitId?: string } | null>;
  status: RetryStatus;
  error: string | undefined;
  canRetry: boolean;
  attemptCount: number;
}

function readAttemptCount(outfitId: string): number {
  if (typeof window === "undefined") return 0;
  const raw = window.sessionStorage.getItem(`regen_attempts:${outfitId}`);
  const n = raw ? parseInt(raw, 10) : 0;
  return Number.isFinite(n) ? n : 0;
}

function bumpAttemptCount(outfitId: string): number {
  if (typeof window === "undefined") return 0;
  const next = readAttemptCount(outfitId) + 1;
  window.sessionStorage.setItem(`regen_attempts:${outfitId}`, String(next));
  return next;
}

export function useRetryAnalysis(outfitId: string | null): UseRetryAnalysisReturn {
  const [status, setStatus] = useState<RetryStatus>("idle");
  const [error, setError] = useState<string | undefined>(undefined);
  const lastFailedAt = useRef<number | null>(null);

  const attemptCount = outfitId ? readAttemptCount(outfitId) : 0;
  const canRetry = !!outfitId && attemptCount < MAX_ATTEMPTS;

  const retry = useCallback(
    async (trigger: RetryTrigger) => {
      if (!outfitId) return null;
      if (status === "retrying") return null;

      const currentAttempts = readAttemptCount(outfitId);
      if (currentAttempts >= MAX_ATTEMPTS) return null;

      // Backoff window
      if (
        lastFailedAt.current &&
        Date.now() - lastFailedAt.current < BACKOFF_WINDOW_MS
      ) {
        await new Promise((r) => setTimeout(r, BACKOFF_DELAY_MS));
      }

      setStatus("retrying");
      setError(undefined);

      try {
        const result = await regenerateOutfit(outfitId);

        if (result.status === "error") {
          lastFailedAt.current = Date.now();
          setStatus("error");
          setError(result.error);
          trackEvent("outfit_retry", {
            outfit_id: outfitId,
            result: "error",
            trigger,
            attempt_number: currentAttempts + 1,
            error: result.error,
          });
          window.setTimeout(() => {
            setStatus("idle");
            setError(undefined);
          }, 3000);
          return null;
        }

        // Successful dispatch (or no-op) → increment counter
        bumpAttemptCount(outfitId);
        setStatus("dispatched");
        trackEvent("outfit_retry", {
          outfit_id: outfitId,
          new_outfit_id: result.newOutfitId,
          result: result.status,
          trigger,
          attempt_number: currentAttempts + 1,
        });
        return { newOutfitId: result.newOutfitId };
      } catch (e) {
        lastFailedAt.current = Date.now();
        const msg = e instanceof Error ? e.message : String(e);
        setStatus("error");
        setError(msg);
        trackEvent("outfit_retry", {
          outfit_id: outfitId,
          result: "error",
          trigger,
          attempt_number: currentAttempts + 1,
          error: msg,
        });
        window.setTimeout(() => {
          setStatus("idle");
          setError(undefined);
        }, 3000);
        return null;
      }
    },
    [outfitId, status],
  );

  return { retry, status, error, canRetry, attemptCount };
}
